/* eslint-disable react/no-unescaped-entities */

/**
 * Panneau "Transmettre à un spécialiste" — visible pour les sessions Vérificateur
 * payées au palier `approfondi` (89 € TTC, transmission incluse).
 *
 * - Liste filtrée de spécialistes éligibles (par profession recommandée).
 * - Sélection + message libre (≤ 400 chars).
 * - Statut courant (pending / accepted / declined / revoked) avec possibilité
 *   de révocation (step-up requis pour utilisateurs authentifiés).
 */
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Send, CheckCircle2, Loader2, RotateCcw, AlertCircle, ShieldCheck } from "lucide-react";
import { useStepUp } from "@/hooks/useStepUp";

const STATUS_LABEL = {
  pending: "En attente du spécialiste",
  accepted: "Acceptée par le spécialiste",
  declined: "Déclinée par le spécialiste",
  revoked: "Révoquée",
};
const STATUS_COLOR = {
  pending: "bg-copper/15 text-copper-deep border border-copper/30",
  accepted: "bg-petrol/15 text-petrol border border-petrol/30",
  declined: "bg-bordeaux/15 text-bordeaux border border-bordeaux/30",
  revoked: "bg-slate-100 text-slate-600 border border-slate-200",
};

export default function VerifierHandoffPanel({ session, isAuthenticated }) {
  const token = session?.session_token;
  const [eligible, setEligible] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [error, setError] = useState(null);
  // Consentements explicites obligatoires (RGPD + transparence intermédiation)
  const [consentTransmission, setConsentTransmission] = useState(false);
  const [consentRole, setConsentRole] = useState(false);
  const { ensureStepUp, StepUpDialog } = useStepUp();

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const [exp, h] = await Promise.all([
        apiClient.get(`/verifier/${token}/eligible-experts`),
        apiClient.get(`/verifier/${token}/handoff`),
      ]);
      setEligible(exp.data?.experts || []);
      setHandoff(h.data && h.data.id ? h.data : null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de charger les spécialistes.");
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const transmit = async () => {
    if (!selectedId) {
      setError("Sélectionnez un spécialiste avant de transmettre.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const headers = {};
      if (isAuthenticated) {
        const stepUp = await ensureStepUp({ purpose: "verifier.transmit", resourceId: token });
        if (!stepUp) { setLoading(false); return; }
        headers["X-Step-Up-Token"] = stepUp;
      }
      const { data } = await apiClient.post(
        `/verifier/${token}/transmit`,
        { expert_id: selectedId, message: message || undefined },
        { headers },
      );
      setHandoff(data);
      setMessage("");
      try {
        const mod = await import("@/lib/analytics");
        mod.trackConversion("handoff", { expert_id: selectedId });
      } catch { /* analytics never blocks user flow */ }
    } catch (e) {
      setError(e?.response?.data?.detail || "La transmission a échoué.");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Confirmer la révocation de cette transmission ? Le spécialiste n'aura plus accès au dossier.")) return;
    setLoading(true);
    setError(null);
    try {
      const reason = window.prompt("Motif (facultatif, max 200 caractères) :") || "";
      const headers = {};
      if (isAuthenticated) {
        const stepUp = await ensureStepUp({ purpose: "verifier.revoke", resourceId: token });
        if (!stepUp) { setLoading(false); return; }
        headers["X-Step-Up-Token"] = stepUp;
      }
      await apiClient.post(`/verifier/${token}/handoff/revoke`, { reason: reason.slice(0, 200) }, { headers });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Révocation impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {StepUpDialog}
      <Card className="p-5" data-testid="verifier-handoff-panel">
        <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Transmission spécialiste · Inclus dans le palier approfondi
        </p>
        <h3 className="font-serif italic text-xl text-night mb-3">Transmettre ce dossier à un spécialiste référencé</h3>

        {handoff && handoff.status !== "revoked" ? (
          <div className="space-y-3" data-testid="handoff-active">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <Badge className={`rounded-sm ${STATUS_COLOR[handoff.status]}`} data-testid="handoff-status">
                  {STATUS_LABEL[handoff.status]}
                </Badge>
                <p className="text-sm text-night mt-2">
                  Expert : <b>{handoff.expert_email || "—"}</b>{" "}
                  <span className="text-night/60">· {handoff.profession || "—"}</span>
                </p>
                <p className="text-[11px] text-night/60 mt-1">
                  Transmis le {new Date(handoff.created_at).toLocaleString("fr-FR")}
                  {handoff.access_count > 0 ? ` · Consulté ${handoff.access_count} fois` : " · Pas encore consulté"}
                </p>
                {handoff.message && (
                  <p className="text-[12px] text-night/75 italic mt-2 px-3 py-2 bg-paper border-l-2 border-copper">
                    « {handoff.message} »
                  </p>
                )}
              </div>
              {handoff.status !== "declined" && (
                <Button
                  variant="outline"
                  onClick={revoke}
                  disabled={loading}
                  data-testid="handoff-revoke-btn"
                  className="rounded-sm border-bordeaux/30 text-bordeaux hover:bg-bordeaux/5"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Révoquer
                </Button>
              )}
            </div>
            {handoff.status === "declined" && (
              <p className="text-[12px] text-night/70 italic">
                Le spécialiste a décliné. Vous pouvez révoquer puis transmettre à un autre.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[12px] text-night/70 leading-relaxed">
              Le rapport sera transmis dans son intégralité (analyse, faits clés, empreinte SHA-256, message libre).
              <b> Aucun frais de mise en relation</b> n'est facturé. Le spécialiste acceptera ou déclinera le dossier.
            </p>

            {eligible.length === 0 ? (
              <Card className="p-4 border-sage bg-paper text-[12px] text-night/70">
                Aucun spécialiste référencé n'est actuellement disponible pour ce type de document.
                Réessayez ultérieurement ou contactez-nous via la page « Nos spécialistes ».
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="eligible-experts">
                {eligible.map((e) => {
                  const active = selectedId === e.id;
                  const sub = (e.subscription || "").toLowerCase();
                  const isPremium = sub === "premium" || sub === "institutionnel";
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setSelectedId(active ? null : e.id)}
                      data-testid={`expert-card-${e.id}`}
                      className={`text-left p-4 rounded-sm border transition-smooth ${
                        active ? "border-copper bg-paper shadow-sm" : "border-sage bg-ivory hover:border-copper"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-night">
                          {e.first_name} {e.last_name}
                        </p>
                        {isPremium && (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-copper">Premium</span>
                        )}
                      </div>
                      <p className="text-[11px] text-copper-deep uppercase tracking-[0.16em] mb-1">
                        {e.profession}
                      </p>
                      <p className="text-[11px] text-night/65">
                        {e.city || "—"}{e.company ? ` · ${e.company}` : ""}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            <textarea
              maxLength={400}
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
              placeholder="Message libre pour le spécialiste (facultatif, 400 caractères max)"
              className="w-full px-3 py-2 rounded-sm border border-sage bg-ivory text-sm focus:outline-none focus:border-copper resize-none"
              rows={3}
              data-testid="handoff-message"
            />

            {error && (
              <div className="flex gap-2 text-[12px] text-bordeaux" data-testid="handoff-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
              </div>
            )}

            {/* Consentements obligatoires (RGPD + transparence rôle CONFILEX) */}
            <div className="space-y-2 rounded-sm border border-sage bg-paper/60 p-3" data-testid="handoff-consents">
              <label className="flex items-start gap-2 text-[12px] text-night/80 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentTransmission}
                  onChange={(e) => setConsentTransmission(e.target.checked)}
                  data-testid="consent-transmission"
                  className="mt-0.5 accent-copper"
                />
                <span>
                  J'accepte que les informations et documents transmis soient communiqués au spécialiste
                  sélectionné afin de permettre l'étude de ma demande et une éventuelle prise de contact.
                </span>
              </label>
              <label className="flex items-start gap-2 text-[12px] text-night/80 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentRole}
                  onChange={(e) => setConsentRole(e.target.checked)}
                  data-testid="consent-role"
                  className="mt-0.5 accent-copper"
                />
                <span>
                  Je comprends que CONFILEX est une plateforme d'intermédiation et ne fournit pas directement
                  de consultation juridique, fiscale, comptable ou patrimoniale.
                </span>
              </label>
            </div>

            <Button
              onClick={transmit}
              disabled={!selectedId || loading || eligible.length === 0 || !consentTransmission || !consentRole}
              data-testid="handoff-transmit-btn"
              className="rounded-sm bg-copper text-ivory hover:bg-copper-deep disabled:opacity-40"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transmission…</>
                : <><Send className="w-4 h-4 mr-2" />Transmettre au spécialiste sélectionné</>}
            </Button>

            <p className="text-[10px] text-night/55 italic leading-relaxed flex items-start gap-1.5">
              <ShieldCheck className="w-3 h-3 mt-0.5 text-petrol shrink-0" />
              La transmission est tracée dans l'historique opposable du dossier
              (date, expert destinataire, IP du demandeur).
            </p>
          </div>
        )}
      </Card>
    </>
  );
}
